import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Pencil, 
  Timer as TimerIcon, 
  Play,
  Eye,
  Settings,
  Code,
  Database,
  Zap,
  CheckCircle,
  AlertCircle,
  Clock,
  Webhook,
  Mail,
  Calendar,
  Image,
  MessageSquare,
  BarChart3,
  FileText,
  Cloud,
  Terminal,
  Cpu,
  Puzzle,
  Search
} from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { useToast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";
import { Switch } from "../components/ui/Switch";
import { useToolsApi } from '../apis/tools-apis';
import { Tool } from '../types';
import { formatHelpText } from '../config/helptext-config';
import { testPageHelpText } from '../config/testtoolpage-helptext-config';
import { FormField } from '../components/FormField';
import { DEFAULT_TOOL_TIMEOUT } from '../config/api-config';

// Tool icon mapping (same as other pages)
const getToolIcon = (tool: Tool) => {
  const name = tool.toolName.toLowerCase();
  const type = tool.toolType?.toLowerCase() || '';
  
  if (name.includes('search') || name.includes('google') || name.includes('bing')) return Search;
  if (name.includes('database') || name.includes('sql') || name.includes('db')) return Database;
  if (name.includes('api') || name.includes('webhook') || name.includes('http')) return Webhook;
  if (name.includes('email') || name.includes('mail') || name.includes('smtp')) return Mail;
  if (name.includes('calendar') || name.includes('schedule') || name.includes('date')) return Calendar;
  if (name.includes('image') || name.includes('photo') || name.includes('picture')) return Image;
  if (name.includes('chat') || name.includes('message') || name.includes('slack')) return MessageSquare;
  if (name.includes('analytics') || name.includes('chart') || name.includes('graph')) return BarChart3;
  if (name.includes('file') || name.includes('document') || name.includes('pdf')) return FileText;
  if (name.includes('cloud') || name.includes('aws') || name.includes('azure')) return Cloud;
  if (name.includes('code') || name.includes('git') || name.includes('github')) return Code;
  if (name.includes('terminal') || name.includes('shell') || name.includes('command')) return Terminal;
  if (name.includes('cpu') || name.includes('process') || name.includes('system')) return Cpu;
  
  return Puzzle;
};
interface TestTimerProps {
  isRunning: boolean;
  toolTimeout?: number | string;
}

const TestTimer: React.FC<TestTimerProps> = ({ isRunning, toolTimeout }) => {
  const [elapsedTime, setElapsedTime] = useState<number | null>(null);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isRunning) {
      // Start from 0 when starting a new test
      setElapsedTime(0);
      
      intervalId = setInterval(() => {
        setElapsedTime(prev => prev !== null ? prev + 1 : 0);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRunning]);

  // Don't render anything if we haven't started timing yet
  if (elapsedTime === null) return null;

  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <TimerIcon className="h-4 w-4" />
      <span>
        {elapsedTime}s / {toolTimeout || 'Default'}s
      </span>
    </div>
  );
};

const TestToolPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const toolsApi = useToolsApi();
  const [tool, setTool] = useState<Tool | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [parameterValues, setParameterValues] = useState<Record<string, string>>({});
  const [isTestingInProgress, setIsTestingInProgress] = useState(false);
  const [testResponse, setTestResponse] = useState<any>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testMode, setTestMode] = useState(true);
  const [isTestRunning, setIsTestRunning] = useState(false);

  const fetchTool = useCallback(async () => {
    if (!id) return;
    
    try {
      const fetchedTool = await toolsApi.getTool(id);
      
      if (fetchedTool) {
        setTool(fetchedTool);
        // Initialize testMode from tool data
        setTestMode(fetchedTool.toolParameters.testMode);
        const initialValues: Record<string, string> = {};
        fetchedTool.toolParameters.parameters.forEach(param => {
          initialValues[param.key] = '';
        });
        setParameterValues(initialValues);
      } else {
        addToast({
          title: "Error",
          description: "Tool not found",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching tool:', error);
      addToast({
        title: "Error",
        description: "Failed to load tool details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, toolsApi, addToast]);

  useEffect(() => {
    let isMounted = true;

    const fetchTool = async () => {
      if (!id) return;
      
      try {
        const fetchedTool = await toolsApi.getTool(id);
        if (!isMounted) return;

        if (fetchedTool) {
          setTool(fetchedTool);
          // Initialize testMode from tool data if available, otherwise keep default true
          // setTestMode(fetchedTool.toolParameters.testMode ?? true);
          setTestMode(true); // Always start with test mode enabled
          const initialValues: Record<string, string> = {};
          fetchedTool.toolParameters.parameters.forEach(param => {
            initialValues[param.key] = '';
          });
          setParameterValues(initialValues);
        } else {
          addToast({
            title: "Error",
            description: "Tool not found",
            variant: "destructive",
          });
        }
      } catch (error) {
        if (!isMounted) return;
        console.error('Error fetching tool:', error);
        addToast({
          title: "Error",
          description: "Failed to load tool details",
          variant: "destructive",
        });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchTool();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleTest = async () => {
    if (!tool) return;
  
    setIsTestingInProgress(true);
    setIsTestRunning(true);
    setTestResponse(null);
    setTestError(null);
  
    try {
      const requestBody: Record<string, any> = {};
      const formattedParameters: Record<string, any> = {};
  
      // Process parameters and create formatted parameter object
      tool.toolParameters.parameters.forEach(param => {
        const value = parameterValues[param.key]?.trim();
        if (value) {
          switch (param.type) {
            case 'int':
              formattedParameters[param.key] = parseInt(value);
              break;
            case 'float':
              formattedParameters[param.key] = parseFloat(value);
              break;
            case 'bool':
              formattedParameters[param.key] = value.toLowerCase() === 'true';
              break;
            case 'list':
            case 'dict':
              try {
                formattedParameters[param.key] = JSON.parse(value);
              } catch {
                formattedParameters[param.key] = value;
              }
              break;
            default: // handles 'str' and any other types
              formattedParameters[param.key] = value;
          }
        }
      });
      console.log('Formatted parameters:', formattedParameters);
      console.log('Test request body:', requestBody);
      requestBody['testMode'] = testMode;
      requestBody['toolTimeout'] = tool.toolTimeout || DEFAULT_TOOL_TIMEOUT;
      requestBody['toolUrlEndpoint'] = tool.toolUrlEndpoint;
      requestBody['toolParameters'] = formattedParameters;
      console.log('Test request body:', requestBody);
  
      const response = await toolsApi.testTool(tool.toolId || '', requestBody);
      console.log('Test response:', response);
  
      // Always display the exact response from the API to the user
      setTestResponse(response);
      
      // If no response, set error message
      if (!response) {
        // Set error message based on response if available
        const errorMessage = 'Test failed. No response received.';
        setTestError(errorMessage);
        
        addToast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        // Clear any previous error
        setTestError(null);
        
        addToast({
          title: "Success",
          description: "Test completed successfully",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Test error:', error);
      setTestError(error instanceof Error ? error.message : 'An unknown error occurred');
      
      addToast({
        title: "Error",
        description: error instanceof Error ? error.message : "Test failed. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsTestingInProgress(false);
      setIsTestRunning(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Spinner className="w-8 h-8 text-blue-500" />
        <p className="text-gray-600">Loading tool details...</p>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="bg-gradient-to-br from-red-100 to-red-200 p-4 rounded-full">
          <Play className="h-8 w-8 text-red-600" />
        </div>
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Tool Not Found</h3>
          <p className="text-gray-600 mb-4">The requested tool could not be found.</p>
          <Button onClick={() => navigate('/studio/tools')}>
            Back to Tools
          </Button>
        </div>
      </div>
    );
  }

  const ToolIcon = getToolIcon(tool);

  return (
    <div className="container mx-auto p-6 max-w-6xl font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/studio/tools')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Tools</span>
            </Button>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate(`/studio/tools/view/${tool.toolId}`)}
              className="group relative bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-6 py-3 rounded-xl font-semibold"
            >
              <div className="flex items-center gap-2">
                <div className="bg-white/20 p-1 rounded-lg group-hover:bg-white/30 transition-colors">
                  <Eye className="h-4 w-4" />
                </div>
                <span>View Tool</span>
              </div>
            </Button>
            
                         <Button
               onClick={() => navigate(`/studio/tools/edit/${tool.toolId}`)}
               className="group relative bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-6 py-3 rounded-xl font-semibold"
             >
              <div className="flex items-center gap-2">
                <div className="bg-white/20 p-1 rounded-lg group-hover:bg-white/30 transition-colors">
                  <Pencil className="h-4 w-4" />
                </div>
                <span>Edit Tool</span>
              </div>
            </Button>
          </div>
        </div>

        {/* Tool Header Card */}
        <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 shadow-lg">
          <CardContent className="p-8">
            <div className="flex items-center gap-6">
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-4 rounded-2xl shadow-lg">
                <ToolIcon className="h-10 w-10 text-white" />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                    Test {tool.toolName}
                  </h1>
                  <Badge className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-sm">
                    <Play className="w-3 h-3 mr-1" />
                    Testing Mode
                  </Badge>
                </div>
                
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  {tool.toolDescription || 'Test this tool with custom parameters and see real-time results.'}
                </p>
                
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <span className="flex items-center gap-2 font-medium">
                    <Settings className="w-4 h-4 text-emerald-500" />
                    {tool.toolParameters?.parameters?.length || 0} parameters
                  </span>
                  <span className="flex items-center gap-2 font-medium">
                    <Clock className="w-4 h-4 text-blue-500" />
                    {tool.toolTimeout || '30'}s timeout
                  </span>
                  <TestTimer 
                    isRunning={isTestRunning} 
                    toolTimeout={tool?.toolTimeout} 
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Configuration & Parameters */}
        <div className="space-y-6">
          {/* Test Mode Configuration */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Zap className="h-5 w-5 text-yellow-600" />
                Test Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">Test Mode</label>
                  <p className="text-sm text-gray-600">Enable safe testing without affecting production systems</p>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={testMode}
                    onCheckedChange={setTestMode}
                    disabled={isTestingInProgress}
                  />
                  <span className={`text-sm font-medium ${testMode ? 'text-emerald-600' : 'text-gray-400'}`}>
                    {testMode ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Input Parameters */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Code className="h-5 w-5 text-blue-600" />
                Input Parameters ({tool.toolParameters?.parameters?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {tool.toolParameters?.parameters && tool.toolParameters.parameters.length > 0 ? (
                <div className="space-y-4">
                  {tool.toolParameters.parameters.map((param) => (
                    <div key={param.key} className="space-y-2">
                      <div className="flex items-center gap-2 mb-1">
                        <label className="text-sm font-semibold text-gray-700">
                          {param.key}
                        </label>
                        <Badge variant="secondary" className="text-xs">
                          {param.type}
                        </Badge>
                      </div>
                      {param.description && (
                        <p className="text-sm text-gray-600 mb-2">{param.description}</p>
                      )}
                      <Input
                        value={parameterValues[param.key] || ''}
                        onChange={(e) => setParameterValues(prev => ({
                          ...prev,
                          [param.key]: e.target.value
                        }))}
                        placeholder={param.example || `Enter ${param.key}`}
                        disabled={isTestingInProgress}
                        className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      />
                      {param.options && (
                        <p className="text-xs text-gray-500">Options: {param.options}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Code className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No input parameters required</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tool Annotation */}
          {tool.toolAnnotation && (
            <Card className="shadow-lg border-gray-200">
              <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <CardTitle className="flex items-center gap-2 text-gray-900">
                  <FileText className="h-5 w-5 text-orange-600" />
                  Tool Instructions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="bg-gray-100 px-4 py-3 rounded-lg text-sm text-gray-800 border leading-relaxed whitespace-pre-wrap">
                  {tool.toolAnnotation}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Test Execution & Results */}
        <div className="space-y-6">
          {/* Test Execution */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Play className="h-5 w-5 text-emerald-600" />
                Test Execution
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="text-center">
                <Button
                  onClick={handleTest}
                  disabled={isTestingInProgress}
                  className="group relative bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-8 py-4 rounded-xl font-semibold text-lg"
                >
                  <div className="flex items-center gap-3">
                    {isTestingInProgress ? (
                      <>
                        <Spinner className="h-5 w-5" />
                        <span>Testing...</span>
                      </>
                    ) : (
                      <>
                        <div className="bg-white/20 p-2 rounded-lg group-hover:bg-white/30 transition-colors">
                          <Play className="h-5 w-5" />
                        </div>
                        <span>Run Test</span>
                      </>
                    )}
                  </div>
                  
                  {/* Subtle glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 -z-10 blur-xl"></div>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Test Results */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Database className="h-5 w-5 text-violet-600" />
                Test Results
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="min-h-[200px]">
                {isTestingInProgress ? (
                  <div className="flex flex-col items-center justify-center h-48 gap-4">
                    <Spinner className="w-8 h-8 text-emerald-500" />
                    <p className="text-gray-600 font-medium">Processing request...</p>
                    <p className="text-sm text-gray-500">This may take up to {tool.toolTimeout || '30'} seconds</p>
                  </div>
                ) : testError ? (
                  <div className="flex flex-col items-center gap-4 p-6 bg-red-50 rounded-lg border border-red-200">
                    <AlertCircle className="h-8 w-8 text-red-600" />
                    <div className="text-center">
                      <h4 className="font-semibold text-red-900 mb-2">Test Failed</h4>
                      <p className="text-sm text-red-700 font-mono bg-red-100 px-3 py-2 rounded border">
                        {testError}
                      </p>
                    </div>
                  </div>
                ) : testResponse ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-emerald-600">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-semibold">Test Completed Successfully</span>
                    </div>
                    <div className="bg-gray-100 rounded-lg border">
                      <div className="bg-gray-200 px-3 py-2 rounded-t-lg border-b">
                        <span className="text-xs font-medium text-gray-700">Response</span>
                      </div>
                      <pre className="p-4 text-sm font-mono text-gray-800 whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto">
                        {JSON.stringify(testResponse, null, 2)}
                      </pre>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 gap-4 text-gray-500">
                    <Database className="h-12 w-12 text-gray-400" />
                    <div className="text-center">
                      <p className="font-medium mb-1">Ready to Test</p>
                      <p className="text-sm">Configure parameters above and click "Run Test" to see results</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TestToolPage;